import { EditorTree } from '../types/types';
import { backendApiService } from './backendApiService';

// Storage keys
const CURRENT_TREE_KEY = 'current_editing_tree';

/**
 * Tree Service for managing the current tree at a tree level
 * 
 * This service is focused on:
 * 1. Loading a tree from the backend or session storage
 * 2. Temporary session storage of the working tree
 * 3. Saving the working tree to backend when requested
 * 4. Exporting the tree in various formats
 * 5. Importing a tree from a JSON file
 * 6. Clearing the current tree from session storage
 */
class TreeService {
  
  /**
   * Get a tree by ID - first tries loading from session storage, then from backend
   */
  getTree(id: string): Promise<EditorTree> {
    return new Promise(async (resolve, reject) => {
      try {
        // First check session storage for active editing tree
        const storedTreeJSON = sessionStorage.getItem(CURRENT_TREE_KEY);
        if (storedTreeJSON) {
          const storedTree: EditorTree = JSON.parse(storedTreeJSON);
          // Only use the stored tree if it's the same ID
          if (storedTree.id === id) {
            console.log('Loading tree from session storage:', id);
            resolve(storedTree);
            return;
          }
        }
        
        // If not in session storage or different ID, load from backend
        console.log('Loading tree from backend:', id);
        const tree = await backendApiService.fetchTree(id);
        
        // Store in session storage for future reference
        this.storeCurrentTree(tree);
        
        resolve(tree);
      } catch (error) {
        console.error('Error loading tree:', error);
        reject(error);
      }
    });
  }

  /**
   * Creates a new tree locally
   */
  createLocalTree(title: string, description: string): EditorTree {
    // Create a new tree with a temporary ID that will be replaced when saved to backend
    const newTree: EditorTree = {
      id: `new`,
      filename: 'new_workspace',
      title: title || 'New Decision Tree',
      description: description || 'A new decision tree',
      nodes: [],
      total_expected_value: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Store in session storage
    this.storeCurrentTree(newTree);
    
    return {...newTree};
  }

  /**
   * Save current tree to session storage (for temporary persistence)
   */
  storeCurrentTree(tree: EditorTree): void {
    try {
      sessionStorage.setItem(CURRENT_TREE_KEY, JSON.stringify(tree));
    } catch (error) {
      console.error('Failed to store current tree in session storage:', error);
    }
  }

  /**
   * Clear the current tree from session storage
   */
  clearCurrentTree(): void {
    try {
      sessionStorage.removeItem(CURRENT_TREE_KEY);
    } catch (error) {
      console.error('Failed to clear current tree from session storage:', error);
    }
  }

  /**
   * Save tree to backend
   */
  async saveTreeToBackend(tree: EditorTree): Promise<EditorTree> {
    try {
      // Save to backend
      const savedTree = await backendApiService.saveTree(tree);
      
      // Update session storage with latest version
      this.storeCurrentTree(savedTree);
      
      return savedTree;
    } catch (error) {
      console.error('Failed to save tree:', error);
      throw error;
    }
  }

  /**
   * Calculate tree expected values - delegates to backend
   */
  async calculateTree(tree: EditorTree): Promise<EditorTree> {
    try {
      const calculatedTree = await backendApiService.calculateTree(tree);
      
      return calculatedTree;
    } catch (error) {
      console.error('Failed to calculate tree:', error);
      throw error;
    }
  }

  /**
   * Delete tree from backend
   */
  async deleteTree(id: string): Promise<void> {
    try {
      await backendApiService.deleteTree(id);
      
      // Clear from session storage if it's the current tree
      const storedTreeJSON = sessionStorage.getItem(CURRENT_TREE_KEY);
      if (storedTreeJSON) {
        const storedTree: EditorTree = JSON.parse(storedTreeJSON);
        if (storedTree.id === id) {
          this.clearCurrentTree();
        }
      }
    } catch (error) {
      console.error('Failed to delete tree:', error);
      throw error;
    }
  }
  
  /**
   * Export tree as JSON file for download
   */
  exportTreeAsJson(tree: EditorTree): void {
    const dataStr = JSON.stringify(tree, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${tree.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  // Export tree as image
  exportTreeAsPng(_tree: EditorTree): void {
    throw new Error('Method not implemented.');
  }

  exportTreeAsSvg(_tree: EditorTree): void {
    throw new Error('Method not implemented.');
  }

  exportTreeAsPDF(_currentTree: EditorTree) {
    throw new Error('Method not implemented.');
  }
  
  /**
   * Import tree from JSON file
   */
  importTreeFromJson(file: File): Promise<EditorTree> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const importedTree: EditorTree = JSON.parse(event.target?.result as string);
          
          // Ensure the tree has a temporary ID until saved to backend
          const newTree: EditorTree = {
            ...importedTree,
            id: `new`,
            updatedAt: new Date().toISOString(),
            importedAt: new Date().toISOString()
          };
          
          // Store in session storage
          this.storeCurrentTree(newTree);
          
          resolve(newTree);
        } catch (error) {
          reject(new Error('Failed to parse imported tree file'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read imported tree file'));
      };
      
      reader.readAsText(file);
    });
  }
}

// Export a singleton instance
export const treeService = new TreeService();